import { endOfDay, startOfDay, subDays } from "date-fns";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
	resetCreateDraftShiftAsyncState,
} from "../../../../../export/shift-scheduler";
import { FilterContext } from "../../../../components/filters/filter-context";
import useSchedulerData from "../../../../components/scheduler/controllers/use-scheduler-data";
import {
  fetchAllJobRoles,
  fetchShiftPurposes,
} from "../../../../components/scheduler/store/scheduler-slice";
import { formatDate, formatDurationInHoursAndMinutes, getLeaveTypeNames } from "../../../../utils/helpers";
import useWorkerRequestsData from "../../controllers/use-worker-requests-data";
import {
  getAllRegularRequests,
  getAllRequestsTypes,
  resetUpdateRequestsStatus,
  setShiftFormData,
} from "../../store/worker-requests-slice";
import {
	RegularShiftRequest,
	RequestStatusTypes,
} from "../../store/worker-requests-types";
import { matchesTableFilters } from "../../utils/filter-utils";

// The shared-filter (Time Off Requests) date range defaults to the last 30
// days. There is no maximum span restriction.
const DEFAULT_RANGE_DAYS = 30;

// Stable empty-array reference so effect dependency checks don't churn when no
// FilterProvider is present (admin mode).
const EMPTY_ARRAY: any[] = [];

const useFullTimeRequestController = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const {
		requestActionStatus,
		hasCoverageDirectAssignmentFailures,
		allRegularRequests,
		fullTimeRequestStatuses,
		attendanceManagement,
		formData,
	} = useWorkerRequestsData();

	// When rendered inside a FilterProvider (Time Off Requests page) the table
	// uses the shared AppliedFilters chips + a date range picker. When rendered
	// without one (admin Worker Requests page) it keeps the legacy status
	// popover + time-range dropdown.
	const filterContext = useContext(FilterContext);
	const useSharedFilters = !!filterContext;
	const appliedFilters = filterContext?.appliedFilters ?? EMPTY_ARRAY;
	const dataRelations = filterContext?.dataRelations ?? EMPTY_ARRAY;

	const [isCreateNewShiftDialogOpen, setIsCreateNewShiftDialogOpen] =
		useState(false);
	const [isShiftDialogOpen, setIsShiftDialogOpen] = useState(false);
	const [shiftDetailsId, setShiftDetailsId] = useState<number | null>(null);
	const [draftShiftDate, setDraftShiftDate] = useState("");
	const [searchQuery, setSearchQuery] = useState<string>("");

	const [isShiftCoverage, setIsShiftCoverage] = React.useState(true);

  const [isEditCallOffOpen, setIsEditCallOffOpen] = useState(false);
  const [isRaiseRequestDialogOpen, setIsRaiseRequestDialogOpen] = useState(false);
  const [selectedRequestToEdit, setSelectedRequestToEdit] = useState<RegularShiftRequest | null>(null);

  const { isShiftPurposeEnabled } = useSchedulerData();

  const handleEditClick = (request: RegularShiftRequest) => {
    setSelectedRequestToEdit(request);
    setIsEditCallOffOpen(true);
  };

  const closeEditDialog = () => {
    setIsEditCallOffOpen(false);
    setSelectedRequestToEdit(null);
  };

  const openRaiseRequestDialog = () => {
    setIsRaiseRequestDialogOpen(true);
  };

  const closeRaiseRequestDialog = () => {
    setIsRaiseRequestDialogOpen(false);
  };

	const handleShiftCoverage = (value: boolean) => {
		setIsShiftCoverage(value);
		const currentDecisionCode = (formData as any)?.decisionCode;
		if (currentDecisionCode === "a" || currentDecisionCode === "r") {
			dispatch(setShiftFormData({ ...formData, decisionCode: value ? "a" : "r" }));
		}
	};
	const [timeFilterOption, setTimeFilterOption] =
		useState<string>("last_30_days");

	// Date range used in shared-filter mode. Defaults to the last 30 days.
	const defaultDateRange = useMemo<[string, string]>(() => {
		return [
			startOfDay(subDays(new Date(), DEFAULT_RANGE_DAYS - 1)).toISOString(),
			endOfDay(new Date()).toISOString(),
		];
	}, []);
	const [dateRange, setDateRange] = useState<[string, string]>(defaultDateRange);

	const handleDateChange = useCallback((value: [string, string]) => {
		setDateRange(value);
	}, []);
	// const filteredRegularRequests = allRegularRequests;
	const openCreateNewShiftDialog = (date: string | null) => {
		if (date) setDraftShiftDate(date);
		dispatch(resetCreateDraftShiftAsyncState());
		setIsCreateNewShiftDialogOpen(true);
	};

	const closeCreateNewShiftDialog = () => {
		setIsCreateNewShiftDialogOpen(false);
		setIsShiftCoverage(true);
	};

	const openShifDetailsDialog = (ids: string) => {
		if (!ids) return;
		const id = Number(ids.split(",")[0]);
		setShiftDetailsId(id);
		setIsShiftDialogOpen(true);
	};

	const closeShiftDetailsDialog = () => {
		setShiftDetailsId(null);
		setIsShiftDialogOpen(false);
	};

	const [filteredRegularRequests, setFilteredRegularRequests] =
		useState<RegularShiftRequest[]>(allRegularRequests);

	const [requestStatus, setRequestStatus] = useState<RequestStatusTypes[]>([]);

	const fetchRequestsWithFilter = useCallback(() => {
		// Shared-filter mode drives the fetch from the date range picker.
		if (useSharedFilters) {
			dispatch(
				getAllRegularRequests({
					startDate: dateRange[0],
					endDate: dateRange[1],
				})
			);
			return;
		}

		const currentDate = new Date();
		const thirtyDaysAgo = new Date();
		thirtyDaysAgo.setDate(currentDate.getDate() - 30);
		const ninetyDaysAgo = new Date();
		ninetyDaysAgo.setDate(currentDate.getDate() - 90);
		const startOfYear = new Date(currentDate.getFullYear(), 0, 1);

		switch (timeFilterOption) {
			case "last_30_days":
				dispatch(
					getAllRegularRequests({
						startDate: thirtyDaysAgo.toISOString(),
						endDate: currentDate.toISOString(),
					})
				);
				break;
			case "last_90_days":
				dispatch(
					getAllRegularRequests({
						startDate: ninetyDaysAgo.toISOString(),
						endDate: currentDate.toISOString(),
					})
				);
				break;
			case "this_year":
				dispatch(
					getAllRegularRequests({
						startDate: startOfYear.toISOString(),
						endDate: currentDate.toISOString(),
					})
				);
				break;
			case "till_today":
				dispatch(getAllRegularRequests({ endDate: currentDate.toISOString() }));
				break;
			default:
				dispatch(getAllRegularRequests({}));
				break;
		}
	}, [dispatch, timeFilterOption, useSharedFilters, dateRange]);

	// Picked Workers that could not be assigned keep the form open on its
	// results screen; its Done button closes it
	useEffect(() => {
		if (requestActionStatus === "fulfilled") {
			if (!hasCoverageDirectAssignmentFailures) closeCreateNewShiftDialog();
			fetchRequestsWithFilter();
		}
	}, [
		requestActionStatus,
		hasCoverageDirectAssignmentFailures,
		dispatch,
		fetchRequestsWithFilter,
	]);

	useEffect(() => {
		const STATUS_NAME_MAP: Record<string, string> = {
			p: t("worker-requests_status-pending"),
			a: t("worker-requests_status-approved"),
			r: t("worker-requests_status-declined"),
			w: t("worker-requests_status-cancelled"),
			aa: t("worker-requests_status-auto-approved"),
		};
		const getAutoApprovedStatus = (): RequestStatusTypes | undefined => {
			return fullTimeRequestStatuses
				.find(status => status.code === "a")
				?.subStatuses?.find(sub => sub.code === "aa");
		};
	
		let autoApprovedStatus = getAutoApprovedStatus();

		if (autoApprovedStatus) {
			autoApprovedStatus = {
				...autoApprovedStatus,
				name: "Auto Approved",
			}
		}
	
		const combinedStatuses = [
			...fullTimeRequestStatuses,
			...(autoApprovedStatus ? [autoApprovedStatus] : [])
		];
	
		const updatedStatuses = combinedStatuses.map((status) => ({
			...status,
			checked: status.code === "p",
			label: STATUS_NAME_MAP[status.code],
		}));
	
		setRequestStatus(updatedStatuses);
	}, [fullTimeRequestStatuses, t]);

	useEffect(() => {
    dispatch(getAllRequestsTypes());
    // Initial setup - only runs once
    if (isShiftPurposeEnabled) {
      dispatch(fetchShiftPurposes());
    }
    dispatch(fetchAllJobRoles());
    // To remove unwanted status on page switch
    dispatch(resetUpdateRequestsStatus());
  }, [dispatch, isShiftPurposeEnabled]);

	useEffect(() => {
		fetchRequestsWithFilter();
	}, [timeFilterOption, fetchRequestsWithFilter]);

	useEffect(() => {
		// Promote 'auto-approved' sub-status to main status
		const regularRequestsWithAutoApproved = allRegularRequests.map(item => {
			if (item.status.code === "a" && item.requestSubStatus?.code === "aa") {
				const { id } = item.requestSubStatus;
				return {
					...item,
					status: { ...item.status, code: "aa", name: "Auto Approved", id },
				};
			}
			return item;
		});

		// Shared-filter mode: match against the AppliedFilters chips (location,
		// work center, shift pattern, status). Legacy mode: filter by the checked
		// status-popover codes.
		let filteredRequestsWithStatus: RegularShiftRequest[];
		if (useSharedFilters) {
			filteredRequestsWithStatus = regularRequestsWithAutoApproved.filter((item) =>
				matchesTableFilters(item, appliedFilters, dataRelations)
			);
		} else {
			const checkedStatusCodes = new Set(
				requestStatus.filter(status => status.checked).map(status => status.code)
			);
			filteredRequestsWithStatus = regularRequestsWithAutoApproved.filter(
				item => checkedStatusCodes.has(item.status.code)
			);
		}

		if (searchQuery === "") {
			setFilteredRegularRequests(filteredRequestsWithStatus);
		} else {
      try {
        const filtered = filteredRequestsWithStatus.filter(
          (item: RegularShiftRequest) => {
            if (!item.ftShift?.startDateTime) {
              return false;
            }
              const date = formatDate({
                dateStr: item.ftShift?.startDateTime,
                formatType: "NUMERIC_DATE",
              });
            const time = formatDate({
              dateStr: item.ftShift?.startDateTime,
              formatType: "TIME_RANGE",
              endDateStr: item.ftShift?.endDateTime,
            });
            const shiftSchedule = `${date} ${time} (${item.ftShift?.patternName})`;
            const startDate = item.startDate;
            const endDate = item.endDate;
            const requestedDate = formatDate({
              dateStr: item.requestDate,
              formatType: "NUMERIC_DATE",
            });
			
			const leaveTypeNames = getLeaveTypeNames(item.leaveTypes);
            const requestTypeName = item.requestType?.name ?? "-";
            const requestType = `${requestTypeName} (${formatDurationInHoursAndMinutes(startDate, endDate)})`;
            const workerComment = item.workerComment ? item.workerComment : "";
			const actionTakenBy = item.status.code === "aa" ? "System Rules" : item.actionTakenBy?.fullName || "-";
            const searchPattern =
              item.worker.firstName +
              item.worker.lastName +
              item.worker.workCenterName +
              shiftSchedule +
              leaveTypeNames.join(" ") +
              requestType +
              workerComment +
              requestedDate +
				actionTakenBy +
              item.status.name;


            return searchPattern
              .toLowerCase()
              .replace(/ /g, "")
              .includes(searchQuery.toLowerCase().replace(/ /g, ""));
          }
        );
        setFilteredRegularRequests(filtered);
      } catch (e) {
        // Do nothing
      }
		}
	}, [
		allRegularRequests,
		requestStatus,
		searchQuery,
		useSharedFilters,
		appliedFilters,
		dataRelations,
	]);

	return [
		{
			isCreateNewShiftDialogOpen,
			isShiftDialogOpen,
			draftShiftDate,
			searchQuery,
			isShiftCoverage,
			rows: filteredRegularRequests,
			requestStatus,
			timeFilterOption,
			shiftDetailsId,
			attendanceManagement,
			isEditCallOffOpen,
			selectedRequestToEdit,
			isRaiseRequestDialogOpen,
			useSharedFilters,
			dateRange,
		},
		{
			openCreateNewShiftDialog,
			closeCreateNewShiftDialog,
			setIsShiftDialogOpen,
			setDraftShiftDate,
			setSearchQuery,
			handleShiftCoverage,
			setRequestStatus,
			setTimeFilterOption,
			setDateRange: handleDateChange,
			openShifDetailsDialog,
			closeShiftDetailsDialog,
			handleEditClick,
			closeEditDialog,
			fetchRequestsWithFilter,
			openRaiseRequestDialog,
			closeRaiseRequestDialog,
		},
	];
};

export default useFullTimeRequestController;
